import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import DialogTitle from 'src/common/components/DialogTitle';
import { generateQrDataUrl } from 'src/helpers/generateQrDataUrl';

interface Props {
  open: boolean;
  onClose: () => void;
  registrationId: string;
  participantName: string;
}

export default function ParticipantQrDialog({
  open,
  onClose,
  registrationId,
  participantName,
}: Props) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [dataUrl, setDataUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !registrationId) {
      setDataUrl(null);
      return;
    }
    let cancelled = false;
    generateQrDataUrl(registrationId)
      .then(url => {
        if (!cancelled) {
          setDataUrl(url);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setDataUrl(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, registrationId]);

  const handleCopyId = async () => {
    try {
      await navigator.clipboard.writeText(registrationId);
      enqueueSnackbar(t('participants.qrIdCopied'), { variant: 'success' });
    } catch {
      enqueueSnackbar(t('participants.qrIdCopyFailed'), { variant: 'error' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle id="participant-qr-title" onClose={onClose}>
        {t('participants.qrCodeTitle', { name: participantName })}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          {dataUrl ? (
            <Box
              component="img"
              src={dataUrl}
              alt={t('participants.qrCodeAlt')}
              sx={{ width: 280, height: 280 }}
            />
          ) : (
            <Typography color="text.secondary">{t('participants.qrLoading')}</Typography>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
            {registrationId}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button startIcon={<ContentCopyIcon />} onClick={handleCopyId}>
          {t('participants.qrCopyId')}
        </Button>
        <Button onClick={onClose}>{t('base.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}
